import { Helmet } from "react-helmet-async";

/**
 * SEOHead — Enterprise-grade, per-page dynamic SEO/AEO/GEO/AIO/SXO component.
 *
 * Generates: <title>, meta description, canonical, OG, Twitter Card,
 * AI-optimized meta, structured data (JSON-LD), breadcrumb schema,
 * speakable schema, and local business schema — all per page.
 */

const BASE_URL = "https://asconsultech.com";
const SITE_NAME = "AS Consultech India Private Limited";
const DEFAULT_IMAGE = `${BASE_URL}/logo-icon.png`;

const LOCAL_BUSINESS_BASE = {
  "@type": "AccountingService",
  name: SITE_NAME,
  url: BASE_URL,
  logo: DEFAULT_IMAGE,
  image: DEFAULT_IMAGE,
  telephone: "+919500121211",
  email: "contact@asconsultech.com",
  priceRange: "$$",
  address: {
    "@type": "PostalAddress",
    streetAddress: "No. 2/582, Anna Nagar, Gandhiji Colony, Kalikkampatti",
    addressLocality: "Dindigul",
    addressRegion: "Tamil Nadu",
    postalCode: "624303",
    addressCountry: "IN",
  },
  geo: {
    "@type": "GeoCoordinates",
    latitude: "10.3624",
    longitude: "77.9695",
  },
  areaServed: [
    { "@type": "Country", name: "India" },
    { "@type": "AdministrativeArea", name: "Tamil Nadu" },
    { "@type": "City", name: "Dindigul" },
    { "@type": "City", name: "Madurai" },
    { "@type": "City", name: "Tiruchirappalli" },
    { "@type": "City", name: "Coimbatore" },
    { "@type": "City", name: "Salem" },
    { "@type": "City", name: "Erode" },
  ],
  serviceArea: [
    { "@type": "City", name: "Dindigul" },
    { "@type": "City", name: "Madurai" },
    { "@type": "City", name: "Tiruchirappalli" },
    { "@type": "City", name: "Coimbatore" },
    { "@type": "City", name: "Salem" },
    { "@type": "City", name: "Erode" },
    { "@type": "AdministrativeArea", name: "Tamil Nadu" },
    { "@type": "Country", name: "India" },
  ],
  openingHoursSpecification: {
    "@type": "OpeningHoursSpecification",
    dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    opens: "09:00",
    closes: "18:00",
  },
};

export default function SEOHead({
  title,
  description,
  canonical,
  ogTitle,
  ogDescription,
  ogImage,
  ogType = "website",
  twitterTitle,
  twitterDescription,
  aiDescription,
  aiKeywords,
  structuredData = [],
  breadcrumbItems = [],
  speakableSelectors,
  noIndex = false,
  articleMeta,
}) {
  const fullCanonical = canonical ? `${BASE_URL}${canonical}` : BASE_URL;
  const fullOgTitle = ogTitle || title;
  const fullOgDesc = ogDescription || description;
  const fullOgImage = ogImage || DEFAULT_IMAGE;
  const fullTwitterTitle = twitterTitle || fullOgTitle;
  const fullTwitterDesc = twitterDescription || fullOgDesc;

  // Build breadcrumb schema
  const breadcrumbSchema = breadcrumbItems.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbItems.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: item.url ? `${BASE_URL}${item.url}` : undefined,
    })),
  } : null;

  // Build speakable schema with @id entity linkage
  const speakableSchema = speakableSelectors ? {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${fullCanonical}#webpage`,
    name: title,
    url: fullCanonical,
    isPartOf: { "@id": "https://asconsultech.com/#website" },
    about: { "@id": "https://asconsultech.com/#organization" },
    inLanguage: "en-IN",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: speakableSelectors,
    },
  } : null;

  // Combine all structured data
  // Only add @context if the schema doesn't already have it (prevents double-wrapping
  // from utility builders like buildLocalBusinessSchema that include @context)
  const allSchemas = [
    ...structuredData.map(sd => sd["@context"] ? sd : { "@context": "https://schema.org", ...sd }),
    breadcrumbSchema,
    speakableSchema,
  ].filter(Boolean);

  return (
    <Helmet>
      {/* Primary Meta */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={fullCanonical} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {!noIndex && <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />}

      {/* Hreflang — per-page international targeting signal */}
      <link rel="alternate" hreflang="en-IN" href={fullCanonical} />
      <link rel="alternate" hreflang="x-default" href={fullCanonical} />

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={fullCanonical} />
      <meta property="og:title" content={fullOgTitle} />
      <meta property="og:description" content={fullOgDesc} />
      <meta property="og:image" content={fullOgImage} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:image:alt" content={`${fullOgTitle} — ${SITE_NAME}`} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_IN" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@asconsultech" />
      <meta name="twitter:creator" content="@asconsultech" />
      <meta name="twitter:title" content={fullTwitterTitle} />
      <meta name="twitter:description" content={fullTwitterDesc} />
      <meta name="twitter:image" content={fullOgImage} />
      <meta name="twitter:image:alt" content={`${fullTwitterTitle} — ${SITE_NAME}`} />

      {/* AIO — AI Optimization Meta */}
      {aiDescription && <meta name="ai.description" content={aiDescription} />}
      {aiKeywords && <meta name="ai.keywords" content={aiKeywords} />}

      {/* Article Meta (for blog posts) */}
      {articleMeta && (
        <>
          <meta property="article:published_time" content={articleMeta.publishedTime} />
          <meta property="article:modified_time" content={articleMeta.modifiedTime || articleMeta.publishedTime} />
          <meta property="article:author" content={articleMeta.author} />
          <meta property="article:section" content={articleMeta.section} />
          <meta property="article:tag" content={articleMeta.section} />
        </>
      )}

      {/* Structured Data (JSON-LD) */}
      {allSchemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

// Export utility for building LocalBusiness schema with per-page customization
export function buildLocalBusinessSchema(overrides = {}) {
  return {
    "@context": "https://schema.org",
    ...LOCAL_BUSINESS_BASE,
    ...overrides,
  };
}

// Export utility for building FAQ schema
export function buildFAQSchema(faqs) {
  return {
    "@type": "FAQPage",
    mainEntity: faqs.map(faq => ({
      "@type": "Question",
      name: faq.q || faq.question || faq.name,
      acceptedAnswer: {
        "@type": "Answer",
        text: faq.a || faq.answer || faq.text,
      },
    })),
  };
}

// Export utility for building Service schema
export function buildServiceSchema(service) {
  return {
    "@type": "Service",
    name: service.title,
    description: service.tagline || service.overview,
    url: `${BASE_URL}/services/${service.slug}`,
    provider: {
      "@type": "Organization",
      name: SITE_NAME,
      url: BASE_URL,
    },
    areaServed: { "@type": "Country", name: "India" },
    serviceType: service.title,
  };
}

// Export utility for building Article schema
export function buildArticleSchema(blog) {
  return {
    "@type": "Article",
    headline: blog.metaTitle || blog.title,
    description: blog.metaDesc || blog.excerpt,
    url: `${BASE_URL}/blogs/${blog.slug}`,
    image: blog.image || DEFAULT_IMAGE,
    datePublished: blog.date,
    dateModified: blog.date,
    author: {
      "@type": "Person",
      name: blog.author,
    },
    publisher: {
      "@type": "Organization",
      name: SITE_NAME,
      logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    },
    mainEntityOfPage: {
      "@type": "WebPage",
      "@id": `${BASE_URL}/blogs/${blog.slug}`,
    },
    articleSection: blog.category,
    wordCount: blog.content ? blog.content.join(" ").split(" ").length : undefined,
  };
}
